import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Link2, RefreshCw, CheckCircle2, XCircle, MousePointerClick } from "lucide-react";

interface CampaignStats {
  sent: number;
  delivered: number;
  uniqueOpens: number;
  opens: number;
  uniqueClicks: number;
  clicks: number;
  softBounces: number;
  hardBounces: number;
  unsubscriptions: number;
  complaints: number;
  openRate: number;
  clickRate: number;
}

interface Campaign {
  id: number;
  name: string;
  subject: string;
  status: string;
  sentDate: string | null;
  stats: CampaignStats;
}

interface LinkResult {
  url: string;
  status: number;
  ok: boolean;
  finalUrl: string;
  ms: number;
  error?: string;
}

interface VisitRow {
  campaign: string | null;
  path: string | null;
  created_at: string;
}

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke("newsletter-campaigns", { body });
  if (error) throw new Error(error.message);
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data;
};

export const NewsletterCampaignsTab = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [links, setLinks] = useState<Record<number, LinkResult[]>>({});
  const [visits, setVisits] = useState<VisitRow[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const data = (await call({ action: "list" })) as { campaigns: Campaign[] };
      setCampaigns(data.campaigns ?? []);
    } catch (e) {
      toast.error(`Nepodařilo se načíst kampaně: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
    const { data: v } = await supabase
      .from("newsletter_visits")
      .select("campaign, path, created_at")
      .order("created_at", { ascending: false })
      .limit(1000);
    setVisits((v as VisitRow[]) ?? []);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (id: number) => {
    setBusy(id);
    try {
      const data = (await call({ action: "verify-links", campaignId: id })) as {
        results: LinkResult[];
        broken: number;
        total: number;
      };
      setLinks((prev) => ({ ...prev, [id]: data.results }));
      if (data.broken === 0) toast.success(`Všech ${data.total} odkazů funguje.`);
      else toast.error(`${data.broken} z ${data.total} odkazů nefunguje.`);
    } catch (e) {
      toast.error(`Ověření odkazů selhalo: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const enableTracking = async (id: number) => {
    setBusy(id);
    try {
      await call({ action: "enable-tracking", campaignId: id });
      toast.success("Sledování prokliků zapnuto (UTM parametry doplněny).");
    } catch (e) {
      toast.error(`Nepodařilo se zapnout sledování: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const visitsFor = (name: string) => {
    const slug = name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    return visits.filter((v) => (v.campaign ?? "") === slug).length;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Newsletter kampaně</h2>
          <p className="text-sm text-muted-foreground">
            Otevření a prokliky z Brevo, návštěvy z newsletteru v aplikaci a kontrola odkazů před odesláním.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Obnovit
        </Button>
      </div>

      {loading && campaigns.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Načítám kampaně…
        </div>
      )}

      {!loading && campaigns.length === 0 && (
        <p className="text-sm text-muted-foreground">Zatím žádné kampaně.</p>
      )}

      {campaigns.map((c) => {
        const s = c.stats;
        const results = links[c.id];
        return (
          <Card key={c.id}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{c.name}</CardTitle>
                  <CardDescription>{c.subject}</CardDescription>
                </div>
                <Badge variant={c.status === "sent" ? "default" : "secondary"}>{c.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                {[
                  ["Odesláno", s.sent],
                  ["Doručeno", s.delivered],
                  ["Otevření", `${s.uniqueOpens} (${s.openRate}%)`],
                  ["Prokliky", `${s.uniqueClicks} (${s.clickRate}%)`],
                  ["Odhlášení", s.unsubscriptions],
                  ["Nedoručeno", s.softBounces + s.hardBounces],
                ].map(([label, value]) => (
                  <div key={label as string} className="rounded-lg border bg-muted/30 p-3">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="text-lg font-semibold">{value}</div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MousePointerClick className="h-4 w-4" />
                Návštěvy aplikace z této kampaně: <strong className="text-foreground">{visitsFor(c.name)}</strong>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => void verify(c.id)} disabled={busy === c.id}>
                  {busy === c.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                  Ověřit odkazy
                </Button>
                <Button size="sm" variant="outline" onClick={() => void enableTracking(c.id)} disabled={busy === c.id}>
                  Zapnout sledování prokliků
                </Button>
              </div>

              {results && (
                <div className="space-y-1 rounded-lg border p-3">
                  {results.length === 0 && <p className="text-sm text-muted-foreground">Kampaň neobsahuje žádné odkazy.</p>}
                  {results.map((r) => (
                    <div key={r.url} className="flex items-start gap-2 text-sm">
                      {r.ok ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                      )}
                      <span className="break-all">{r.url}</span>
                      <span className="ml-auto shrink-0 text-muted-foreground">{r.status || r.error || "chyba"}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
